import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs/operators';
import { ApiResult } from 'src/app/models/ApiResult';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { AgreementImportScope, ImportFromExternalSystemRequest, ImportFromExternalSystemResponse } from './import-from-external-system.model';
import { ImportFromExternalSystemService } from './import-from-external-system.service';

@Component({
    selector: 'app-import-from-external-system',
    templateUrl: './import-from-external-system.component.html',
    styleUrls: ['./import-from-external-system.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ImportFromExternalSystemComponent implements OnInit {
    private formBuilder = inject(FormBuilder);
    private importService = inject(ImportFromExternalSystemService);
    private spinner = inject(NgxSpinnerService);
    private cdr = inject(ChangeDetectorRef);
    private snackbar = inject(SnackBarService);

    importForm!: FormGroup;
    successMessage: string | null = null;
    errorMessages: string[] = [];

    readonly AgreementImportScope = AgreementImportScope;

    ngOnInit(): void {
        this.importForm = this.formBuilder.group({
            registrationNumber: ['', [Validators.required]],
            cardNumber: ['0', [Validators.required]],
            importAddress: [false],
            draft: [false],
            // Defaults per HDP-16251: employment imports the whole employee history, mandate stays opt-in.
            employmentAgreementScope: [AgreementImportScope.AllFromAllRegistries],
            mandateAgreementScope: [AgreementImportScope.None],
        });
    }

    // Gating removed: deployment of this frontend change must be sequenced after TalentPoint-API support (HDP-16251).
    onImport(): void {
        if (this.importForm.invalid) {
            this.importForm.markAllAsTouched();
            return;
        }

        this.successMessage = null;
        this.errorMessages = [];
        this.spinner.show();

        const request: ImportFromExternalSystemRequest = {
            RegistrationNumber: this.importForm.value.registrationNumber,
            CardNumber: this.importForm.value.cardNumber,
            ImportAddress: this.importForm.value.importAddress,
            Draft: this.importForm.value.draft,
            EmploymentAgreementScope: this.importForm.value.employmentAgreementScope,
            MandateAgreementScope: this.importForm.value.mandateAgreementScope,
        };

        this.importService.import(request).pipe(
            finalize(() => {
                this.spinner.hide();
                this.cdr.markForCheck();
            })
        ).subscribe({
            next: (result: ApiResult<ImportFromExternalSystemResponse>) => {
                const response = result.Value ?? result as unknown as ImportFromExternalSystemResponse;
                if (response.IsSuccess) {
                    this.successMessage = 'ImportQueued';
                } else {
                    this.errorMessages = this.toErrorMessages(response.ErrorCodes);
                    this.snackbar.openErrorMultipleSnackBarInRow([...this.errorMessages]);
                }
            },
            error: (err) => {
                const body = err?.error;
                let errors: string[] = ['UnknownError'];

                if (typeof body === 'string') {
                    errors = this.toErrorMessages(body);
                } else {
                    const response = body?.Value ?? body;
                    errors = this.toErrorMessages(response?.ErrorCodes ?? response?.Error ?? 'UnknownError');
                }

                this.errorMessages = errors;
                this.snackbar.openErrorMultipleSnackBarInRow([...errors]);
            },
        });
    }

    private toErrorMessages(input: string | string[] | null | undefined): string[] {
        if (!input) return ['UnknownError'];
        return Array.isArray(input) ? input : [input];
    }
}
